import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, ShoppingCart, BarChart3, Settings, FileText, Mail, Calendar, Users, FolderKanban, Layers, Zap, Shield, Cloud, Database, ArrowUpDown, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, RotateCcw } from 'lucide-react';
import { marketApi } from '@/api';
import type { System, Plan } from '@/api/market';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  adminDrawerFooterClass,
  adminDrawerFormPaddingClass,
  adminDrawerScrollAreaClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  adminDrawerTitleClass,
  alertDialogDarkClass,
  alertDialogDescDark,
  alertDialogTitleDark,
  btnDangerOutline,
  btnDangerSolid,
  btnOutlineNeon,
  btnPrimaryNeon,
  fieldClass,
  labelMutedClass,
} from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';
import { useUIStore } from '@/store/useUIStore';

type SortField = 'sortOrder' | 'name' | 'status' | 'createdAt';
type SortOrder = 'asc' | 'desc';

export function MarketConfigPage() {
  const canMutateMarket = usePlatformPermission('platform:market:mutate');
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-500/40 bg-cyan-500 text-slate-950 hover:bg-cyan-400';
  const lightOutlineBtn =
    'border border-slate-300 bg-white text-slate-700 hover:border-cyan-400/45 hover:bg-cyan-50 hover:text-cyan-700';
  const lightDangerOutline =
    'border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 hover:border-rose-400';
  const lightDrawerField =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35';
  const [apps, setApps] = useState<System[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<System | null>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    icon: '',
    url: '',
    planKeys: [] as string[],
  });

  // 排序和分页状态
  const [sortField, setSortField] = useState<SortField>('sortOrder');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(3); // 每页显示数量，默认3个
  const pageSizeOptions = [3, 6, 9, 12]; // 可选的每页数量

  // 预设图标列表
  const presetIcons = [
    { name: '购物车', value: 'ShoppingCart', component: ShoppingCart },
    { name: '图表', value: 'BarChart3', component: BarChart3 },
    { name: '设置', value: 'Settings', component: Settings },
    { name: '文档', value: 'FileText', component: FileText },
    { name: '邮件', value: 'Mail', component: Mail },
    { name: '日历', value: 'Calendar', component: Calendar },
    { name: '用户', value: 'Users', component: Users },
    { name: '项目', value: 'FolderKanban', component: FolderKanban },
    { name: '层级', value: 'Layers', component: Layers },
    { name: '闪电', value: 'Zap', component: Zap },
    { name: '安全', value: 'Shield', component: Shield },
    { name: '云端', value: 'Cloud', component: Cloud },
    { name: '数据库', value: 'Database', component: Database },
  ];

  const isLoadedRef = useRef(false);

  useEffect(() => {
    // 防止 React StrictMode 导致的重复请求
    if (isLoadedRef.current) return;
    isLoadedRef.current = true;
    
    loadApps();
    loadPlans();
  }, []);

  async function loadApps() {
    try {
      setLoading(true);
      const data = await marketApi.getSystems();
      setApps(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('加载应用列表失败:', error);
      setApps([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadPlans() {
    try {
      const data = await marketApi.getAllPlans();
      // 将价格从分转换为元
      const plansWithYuan = (Array.isArray(data) ? data : []).map(plan => ({
        ...plan,
        price: plan.price / 100,
      }));
      setAvailablePlans(plansWithYuan);
    } catch (error) {
      console.error('加载订阅版本失败:', error);
      setAvailablePlans([]);
    }
  }

  // 排序和分页逻辑
  const { paginatedApps, totalPages } = useMemo(() => {
    // 过滤掉默认系统（key 为 'default' 的内部系统）
    const filteredApps = apps.filter(app => app.key !== 'default');
    
    // 默认按后端返回的 sortOrder 排序（已排序）
    const sorted = [...filteredApps].sort((a, b) => {
      if (sortField === 'sortOrder') {
        return a.sortOrder - b.sortOrder;
      }
      
      let comparison = 0;

      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'zh-CN');
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    const total = Math.ceil(sorted.length / pageSize);
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;

    return {
      paginatedApps: sorted.slice(start, end),
      totalPages: total,
    };
  }, [apps, sortField, sortOrder, currentPage, pageSize]);

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);
  }

  async function moveApp(index: number, direction: 'up' | 'down') {
    const newApps = [...apps];
    
    if (direction === 'up' && index > 0) {
      [newApps[index], newApps[index - 1]] = [newApps[index - 1], newApps[index]];
    } else if (direction === 'down' && index < newApps.length - 1) {
      [newApps[index], newApps[index + 1]] = [newApps[index + 1], newApps[index]];
    } else {
      return;
    }

    // 更新 sortOrder 并保存到后端
    const orders = newApps.map((app, i) => ({
      id: app.id,
      sortOrder: i,
    }));

    try {
      await marketApi.reorderSystems(orders);
      setApps(newApps.map(app => ({ ...app, sortOrder: orders.find(o => o.id === app.id)?.sortOrder ?? app.sortOrder })));
    } catch (error) {
      console.error('更新排序失败:', error);
      toast.error('更新排序失败，请重试');
    }
  }

  function handleAdd() {
    setEditingApp(null);
    setFormData({
      name: '',
      key: '',
      description: '',
      icon: '',
      url: '',
      planKeys: [],
    });
    // 打开对话框前重新加载 plans，确保数据是最新的
    loadPlans().then(() => setDrawerOpen(true));
  }

  function handleEdit(app: System) {
    setEditingApp(app);
    setFormData({
      name: app.name,
      key: app.key,
      description: app.description || '',
      icon: app.icon || '',
      url: app.url || '',
      planKeys: app.plans?.map(p => p.key) || [],
    });
    // 打开对话框前重新加载 plans，确保数据是最新的
    loadPlans().then(() => setDrawerOpen(true));
  }

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<System | null>(null);

  function handleDeleteClick(app: System) {
    setAppToDelete(app);
    setDeleteDialogOpen(true);
  }

  async function confirmDelete() {
    if (!appToDelete) return;
    try {
      await marketApi.deleteSystem(appToDelete.id);
      setApps(apps.filter(a => a.id !== appToDelete.id));
      setDeleteDialogOpen(false);
      setAppToDelete(null);
    } catch (error) {
      console.error('删除应用失败:', error);
      toast.error('删除失败，请重试');
    }
  }

  async function toggleListing(id: string) {
    try {
      await marketApi.toggleSystemListing(id);
      // 重新加载所有数据，确保获取到最新的完整数据
      await loadApps();
    } catch (error) {
      console.error('切换上下架状态失败:', error);
      toast.error('操作失败，请重试');
    }
  }

  async function handleSubmit() {
    try {
      if (editingApp) {
        // 编辑模式
        await marketApi.updateSystem({
          id: editingApp.id,
          name: formData.name,
          key: formData.key,
          description: formData.description,
          icon: formData.icon,
          url: formData.url,
          planKeys: formData.planKeys,
        });
        // 按 formData.planKeys 的顺序排序 plans
        const sortedPlans = formData.planKeys
          .map(key => availablePlans.find(p => p.key === key))
          .filter((p): p is Plan => p !== undefined);
        
        setApps(apps.map(app => 
          app.id === editingApp.id 
            ? { 
                ...app, 
                name: formData.name,
                key: formData.key,
                description: formData.description,
                icon: formData.icon,
                url: formData.url,
                plans: sortedPlans,
                updatedAt: new Date().toISOString(),
              }
            : app
        ));
      } else {
        // 新增模式
        const newApp = await marketApi.createSystem({
          name: formData.name,
          key: formData.key,
          description: formData.description,
          icon: formData.icon,
          url: formData.url,
          planKeys: formData.planKeys,
        });
        // 手动设置 plans，因为后端返回的数据可能不包含完整的 plans 信息
        // 按 formData.planKeys 的顺序排序
        const sortedPlans = formData.planKeys
          .map(key => availablePlans.find(p => p.key === key))
          .filter((p): p is Plan => p !== undefined);
        const appWithPlans = {
          ...newApp,
          plans: sortedPlans,
        };
        setApps([...apps, appWithPlans]);
      }
      
      setDrawerOpen(false);
    } catch (error) {
      console.error('保存应用失败:', error);
      toast.error('保存失败，请重试');
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">应用市场配置</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            管理系统应用的上架与订阅版本配置
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
            onClick={loadApps}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            刷新
          </Button>
          {canMutateMarket && (
            <Button
              className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={handleAdd}
            >
              <Plus className="mr-2 h-4 w-4" />
              添加应用
            </Button>
          )}
        </div>
      </div>

      {/* 排序工具栏（深色霓虹，与主布局一致） */}
      {!loading && apps.length > 0 && (
        <div
          className={cn(
            isLight
              ? 'flex flex-col gap-3 rounded-xl border border-slate-300 bg-white p-4 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.25)]'
              : 'flex flex-col gap-3 rounded-xl border border-cyan-500/15 bg-zinc-900/55 p-4 shadow-[0_0_0_1px_rgba(34,211,238,0.06)] backdrop-blur-md',
            'sm:flex-row sm:flex-wrap sm:items-center sm:gap-2',
          )}
        >
          <span className={cn('shrink-0 text-sm font-medium', isLight ? 'text-slate-500' : 'text-zinc-400')}>
            排序：
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant={sortField === 'sortOrder' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('sortOrder')}
              className={cn(
                'gap-1',
                sortField === 'sortOrder' && (isLight ? lightPrimaryBtn : btnPrimaryNeon),
                sortField !== 'sortOrder' && (isLight ? lightOutlineBtn : btnOutlineNeon),
              )}
            >
              自定义排序
              {sortField === 'sortOrder' && (
                <ArrowUpDown className={cn('h-3 w-3', sortOrder === 'desc' && 'rotate-180')} />
              )}
            </Button>
            <Button
              variant={sortField === 'name' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('name')}
              className={cn(
                'gap-1',
                sortField === 'name' && (isLight ? lightPrimaryBtn : btnPrimaryNeon),
                sortField !== 'name' && (isLight ? lightOutlineBtn : btnOutlineNeon),
              )}
            >
              名称
              {sortField === 'name' && (
                <ArrowUpDown className={cn('h-3 w-3', sortOrder === 'desc' && 'rotate-180')} />
              )}
            </Button>
            <Button
              variant={sortField === 'status' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('status')}
              className={cn(
                'gap-1',
                sortField === 'status' && (isLight ? lightPrimaryBtn : btnPrimaryNeon),
                sortField !== 'status' && (isLight ? lightOutlineBtn : btnOutlineNeon),
              )}
            >
              状态
              {sortField === 'status' && (
                <ArrowUpDown className={cn('h-3 w-3', sortOrder === 'desc' && 'rotate-180')} />
              )}
            </Button>
            <Button
              variant={sortField === 'createdAt' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleSort('createdAt')}
              className={cn(
                'gap-1',
                sortField === 'createdAt' && (isLight ? lightPrimaryBtn : btnPrimaryNeon),
                sortField !== 'createdAt' && (isLight ? lightOutlineBtn : btnOutlineNeon),
              )}
            >
              创建时间
              {sortField === 'createdAt' && (
                <ArrowUpDown className={cn('h-3 w-3', sortOrder === 'desc' && 'rotate-180')} />
              )}
            </Button>
          </div>
          <span className={cn('text-sm sm:ml-auto', isLight ? 'text-slate-500' : 'text-zinc-500')}>
            共 {apps.length} 个应用（悬停卡片可调整顺序）
          </span>
        </div>
      )}

      {/* 应用列表 - 卡片式布局 */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">加载中...</div>
      ) : apps.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          暂无应用数据
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {paginatedApps.map((app) => {
              const actualIndex = apps.findIndex(a => a.id === app.id);
              return (
            <Card key={app.id} className="relative group">
              {/* 排序按钮 - 悬停显示 */}
              {canMutateMarket && (
                <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-7 w-7 shadow-sm',
                      isLight
                        ? 'border-slate-300 bg-white text-slate-600 hover:border-cyan-400/40 hover:bg-cyan-50 hover:text-cyan-700'
                        : 'border-zinc-600/80 bg-zinc-900/90 text-zinc-200 hover:border-cyan-400/40 hover:bg-cyan-500/20 hover:text-cyan-50',
                    )}
                    onClick={() => moveApp(actualIndex, 'up')}
                    disabled={actualIndex === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className={cn(
                      'h-7 w-7 shadow-sm',
                      isLight
                        ? 'border-slate-300 bg-white text-slate-600 hover:border-cyan-400/40 hover:bg-cyan-50 hover:text-cyan-700'
                        : 'border-zinc-600/80 bg-zinc-900/90 text-zinc-200 hover:border-cyan-400/40 hover:bg-cyan-500/20 hover:text-cyan-50',
                    )}
                    onClick={() => moveApp(actualIndex, 'down')}
                    disabled={actualIndex === apps.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
              )}

              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* 应用图标 */}
                    <div
                      className={cn(
                        'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border p-2',
                        isLight
                          ? 'border-slate-300 bg-slate-50'
                          : 'border-zinc-700/50 bg-zinc-800/60',
                      )}
                    >
                      {app.icon ? (
                        app.icon.startsWith('http') ? (
                          <img src={app.icon} alt={app.name} className="w-full h-full object-contain" />
                        ) : (
                          <div className="w-full h-full bg-primary/10 rounded flex items-center justify-center text-primary">
                            {(() => {
                              const IconComponent = presetIcons.find(p => p.value === app.icon)?.component;
                              return IconComponent ? <IconComponent className="w-8 h-8" /> : <span className="text-2xl font-bold">{app.name.charAt(0)}</span>;
                            })()}
                          </div>
                        )
                      ) : (
                        <div className="w-full h-full bg-primary/10 rounded flex items-center justify-center text-primary font-bold text-lg">
                          {app.name.charAt(0)}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-lg truncate">{app.name}</CardTitle>
                        <Switch
                          checked={app.status === 'active'}
                          onCheckedChange={() => toggleListing(app.id)}
                          disabled={!canMutateMarket}
                          className="flex-shrink-0"
                        />
                      </div>
                      <CardDescription className="truncate">{app.key}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                <div className="space-y-3">
                  {/* 应用描述 */}
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {app.description || '暂无描述'}
                  </p>

                  {/* 订阅版本 */}
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground font-medium">订阅版本：</div>
                    <div className="flex flex-wrap gap-1">
                      {app.plans && app.plans.length > 0 ? (
                        app.plans.map((plan) => (
                          <Badge key={plan.id} variant="secondary" className="text-xs">
                            {plan.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-xs">无</span>
                      )}
                    </div>
                  </div>

                  {/* 操作按钮 */}
                  <div className={cn('flex gap-2 border-t pt-3', isLight ? 'border-slate-200' : 'border-zinc-800/80')}>
                    {canMutateMarket ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn('flex-1', isLight ? lightOutlineBtn : btnOutlineNeon)}
                          onClick={() => handleEdit(app)}
                        >
                          <Pencil className="mr-1 h-4 w-4" />
                          编辑
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className={cn('flex-1', isLight ? lightDangerOutline : btnDangerOutline)}
                          onClick={() => handleDeleteClick(app)}
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          删除
                        </Button>
                      </>
                    ) : (
                      <p className={cn('w-full text-center text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>无市场配置写权限</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>

        {/* 分页控件 */}
        <div className={cn('mt-6 flex items-center justify-between border-t pt-4', isLight ? 'border-slate-200' : 'border-zinc-800/80')}>
          {/* 每页数量选择 */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">每页显示：</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1); // 切换每页数量时重置到第一页
              }}
              className={cn(
                'h-8 rounded-md border px-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:ring-offset-0',
                isLight
                  ? 'border-slate-300 bg-white text-slate-700'
                  : 'border-zinc-700/80 bg-zinc-950/60 text-zinc-200 focus:ring-offset-zinc-950',
              )}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} 条
                </option>
              ))}
            </select>
          </div>

          {/* 分页导航 */}
          {totalPages > 1 && (
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="text-sm text-muted-foreground">
                第 {currentPage} / {totalPages} 页
              </span>

              <Button
                variant="outline"
                size="sm"
                className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </>
      )}

      {/* 配置说明 */}
      <Card>
        <CardHeader>
          <CardTitle>配置说明</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>• 使用右上角开关控制应用上下架状态</p>
          <p>• 已上架的应用会在租户的应用市场中显示</p>
          <p>• 可以为每个应用配置可购买的订阅版本（试用版/基础版/专业版）</p>
          <p>• 租户只能购买已上架应用的已配置版本</p>
          <p>• 下架应用不会影响已购买的租户，但新租户无法购买</p>
        </CardContent>
      </Card>

      {/* 新增/编辑抽屉 */}
      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        direction="right"
        shouldScaleBackground={false}
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('wide'),
            isLight &&
              'border-l border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/40 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700',
          )}
          data-admin-shell
        >
          <DrawerHeader className={cn(adminDrawerHeaderClass, isLight && 'border-b border-slate-200 bg-white')}>
            <DrawerTitle className={cn(adminDrawerTitleClass, isLight && 'text-slate-900')}>
              {editingApp ? '编辑应用' : '新增应用'}
            </DrawerTitle>
            <DrawerDescription className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-500')}>
              {editingApp ? '修改应用信息' : '创建新的应用'}
            </DrawerDescription>
          </DrawerHeader>

          <div className={adminDrawerScrollAreaClass}>
            <div className={cn(adminDrawerFormPaddingClass, 'space-y-4')}>
            <div className="grid gap-2">
              <Label htmlFor="drawer-name" className={labelMutedClass}>
                应用名称
              </Label>
              <Input
                id="drawer-name"
                placeholder="例如：CRM 系统"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={cn(fieldClass, isLight && lightDrawerField)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="drawer-key" className={labelMutedClass}>
                应用标识
              </Label>
              <Input
                id="drawer-key"
                placeholder="例如：crm"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                className={cn(fieldClass, isLight && lightDrawerField)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="drawer-icon" className={labelMutedClass}>
                图标
              </Label>
              <div className="flex gap-2">
                <Input
                  id="drawer-icon"
                  placeholder="选择图标或输入 URL"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className={cn(fieldClass, 'min-w-0 flex-1', isLight && lightDrawerField)}
                />
                <Button
                  type="button"
                  variant="outline"
                  className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                  onClick={() => setShowIconPicker(!showIconPicker)}
                >
                  选择
                </Button>
              </div>
              
              {/* 图标选择器 */}
              {showIconPicker && (
                <div
                  className={cn(
                    'grid max-h-64 grid-cols-5 gap-2 overflow-y-auto rounded-md border p-3',
                    isLight
                      ? 'border-slate-300 bg-slate-50'
                      : 'border-zinc-700/60 bg-zinc-900/70',
                  )}
                >
                  {presetIcons.map((preset) => {
                    const IconComponent = preset.component;
                    return (
                      <button
                        key={preset.value}
                        type="button"
                        className={cn(
                          'flex flex-col items-center justify-center rounded-md p-2 transition-colors',
                          isLight
                            ? 'hover:bg-cyan-50 hover:text-cyan-700'
                            : 'hover:bg-cyan-500/10 hover:text-cyan-50',
                          formData.icon === preset.value &&
                            (isLight
                              ? 'bg-cyan-500 text-slate-900 shadow-[0_6px_18px_-12px_rgba(8,145,178,0.4)]'
                              : 'bg-primary text-primary-foreground shadow-[0_0_16px_-6px_rgba(34,211,238,0.45)]'),
                        )}
                        onClick={() => {
                          setFormData({ ...formData, icon: preset.value });
                          setShowIconPicker(false);
                        }}
                      >
                        <IconComponent className="w-6 h-6 mb-1" />
                        <span className="text-xs">{preset.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              
              <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                可选择预设图标，或输入自定义 URL
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="drawer-url" className={labelMutedClass}>
                应用 URL
              </Label>
              <Input
                id="drawer-url"
                placeholder="例如：https://crm.example.com"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className={cn(fieldClass, isLight && lightDrawerField)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="drawer-description" className={labelMutedClass}>
                应用描述
              </Label>
              <Textarea
                id="drawer-description"
                placeholder="应用描述..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className={cn(fieldClass, isLight && lightDrawerField)}
              />
            </div>

            {/* 订阅版本选择 */}
            <div className="grid gap-2">
              <Label className={labelMutedClass}>订阅版本</Label>
              <div className="grid max-h-72 grid-cols-2 gap-3 overflow-y-auto pr-1">
                {availablePlans.length > 0 ? (
                  availablePlans.map((plan) => {
                    const isChecked = formData.planKeys.includes(plan.key);
                    return (
                      <div
                        key={plan.id}
                        className={cn(
                          'relative flex min-h-28 cursor-pointer flex-col rounded-lg border-2 p-3 transition-all',
                          isChecked
                            ? isLight
                              ? 'border-cyan-500/45 bg-cyan-50 shadow-[0_8px_22px_-16px_rgba(8,145,178,0.3)]'
                              : 'border-cyan-400/50 bg-cyan-500/10 shadow-[0_0_24px_-12px_rgba(34,211,238,0.28)]'
                            : isLight
                              ? 'border-slate-300 bg-white hover:border-cyan-400/45'
                              : 'border-zinc-700/80 bg-zinc-900/45 hover:border-cyan-500/35',
                        )}
                        onClick={() => {
                          if (isChecked) {
                            setFormData({
                              ...formData,
                              planKeys: formData.planKeys.filter(k => k !== plan.key),
                            });
                          } else {
                            setFormData({
                              ...formData,
                              planKeys: [...formData.planKeys, plan.key],
                            });
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className={cn('truncate text-sm font-medium', isLight ? 'text-slate-900' : 'text-zinc-100')}>
                              {plan.name}
                            </div>
                            <div className={cn('mt-1 text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                              ¥{plan.price}
                              <span className="text-xs">/月</span>
                            </div>
                          </div>
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setFormData({
                                  ...formData,
                                  planKeys: [...formData.planKeys, plan.key],
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  planKeys: formData.planKeys.filter(k => k !== plan.key),
                                });
                              }
                            }}
                            className="pointer-events-none"
                          />
                        </div>
                        {plan.description && (
                          <div className={cn('mt-2 line-clamp-2 text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                            {plan.description}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-2 flex items-center justify-center py-8 text-center">
                    <div className="space-y-2">
                      <div className="text-4xl">📦</div>
                      <p className={cn('text-sm', isLight ? 'text-slate-500' : 'text-zinc-400')}>暂无订阅版本</p>
                      <p className={cn('text-xs', isLight ? 'text-slate-400' : 'text-zinc-500')}>
                        请先在「订阅版本配置」中创建
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>

          <div className={cn(adminDrawerFooterClass, isLight && 'border-t border-slate-200 bg-white')}>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canMutateMarket}
              className={cn('flex-1', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
            >
              {editingApp ? '保存修改' : '创建应用'}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* 删除确认 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent
          className={cn(
            isLight ? 'border-slate-300 bg-white text-slate-900' : alertDialogDarkClass,
          )}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(isLight ? 'text-slate-900' : alertDialogTitleDark)}>
              确认删除
            </AlertDialogTitle>
            <AlertDialogDescription className={cn(isLight ? 'text-slate-500' : alertDialogDescDark)}>
              确定要删除应用 &quot;{appToDelete?.name}&quot; 吗？此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
              onClick={() => setAppToDelete(null)}
            >
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className={btnDangerSolid}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
